'use client';

import React, { useState, useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: string;
  quote: string;
  author: string;
  position: string;
  company: string;
  image: string;
  alt: string;
  rating: number;
  videoUrl?: string;
}

interface TestimonialCarouselProps {
  testimonials: Testimonial[];
}

const TestimonialCarousel = ({ testimonials }: TestimonialCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Hydration check
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Auto-play functionality: move right to left every 5 seconds
  useEffect(() => {
    if (isPaused || expandedId) {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
        autoPlayRef.current = null;
      }
      return;
    }

    autoPlayRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isPaused, expandedId, testimonials.length]);

  // Keyboard navigation
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (expandedId !== null) {
        if (e.key === 'Escape') {
          closeModal();
        }
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigatePrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateNext();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleCardClick(testimonials[currentIndex].id, 0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, expandedId, testimonials]);

  const navigateNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    resetAutoPlay();
  };

  const navigatePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    resetAutoPlay();
  };

  const resetAutoPlay = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    if (!isPaused && !expandedId) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }, 5000);
    }
  };

  const handleCardClick = (id: string, position: number = 0) => {
    if (position === 0) {
      // Center card - expand
      setExpandedId(id);
    } else {
      // Side card - rotate to center
      const clickedIndex = testimonials.findIndex(t => t.id === id);
      setCurrentIndex(clickedIndex);
      resetAutoPlay();
    }
  };

  const closeModal = () => {
    setExpandedId(null);
  };

  // Touch/Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      navigateNext();
    } else if (isRightSwipe) {
      navigatePrev();
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  // Calculate card position relative to center (0 = center, -2 to +2)
  const getRelativePosition = (index: number): number => {
    const diff = index - currentIndex;
    if (diff > testimonials.length / 2) return diff - testimonials.length;
    if (diff < -testimonials.length / 2) return diff + testimonials.length;
    return diff;
  };

  // Get card styling based on position (-2, -1, 0, +1, +2)
  const getCardStyle = (position: number) => {
    const absPos = Math.abs(position);
    
    // Hide cards beyond position ±2
    if (absPos > 2) {
      return {
        opacity: 0,
        transform: `translateX(${position > 0 ? '200%' : '-200%'}) scale(0.5)`,
        zIndex: 0,
        pointerEvents: 'none' as const,
        filter: 'blur(4px)',
      };
    }

    let scale = 1.0;
    let translateX = 0;
    let translateZ = 0;
    let rotateY = 0;
    let opacity = 1;
    let zIndex = 100;
    let blur = 0;

    switch (position) {
      case 0: // Center card
        scale = 1.0;
        translateX = 0;
        translateZ = 50;
        rotateY = 0;
        opacity = 1;
        zIndex = 100;
        blur = 0;
        break;
      case -1: // Left adjacent
        scale = 0.8;
        translateX = -60;
        translateZ = 0;
        rotateY = 3;
        opacity = 0.75;
        zIndex = 50;
        blur = 0;
        break;
      case 1: // Right adjacent
        scale = 0.8;
        translateX = 60;
        translateZ = 0;
        rotateY = -3;
        opacity = 0.75;
        zIndex = 50;
        blur = 0;
        break;
      case -2: // Far left
        scale = 0.6;
        translateX = -100;
        translateZ = -20;
        rotateY = 5;
        opacity = 0.5;
        zIndex = 25;
        blur = 2;
        break;
      case 2: // Far right
        scale = 0.6;
        translateX = 100;
        translateZ = -20;
        rotateY = -5;
        opacity = 0.5;
        zIndex = 25;
        blur = 2;
        break;
    }

    return {
      transform: `translateX(${translateX}%) translateZ(${translateZ}px) scale(${scale}) rotateY(${rotateY}deg)`,
      opacity,
      zIndex,
      filter: blur > 0 ? `blur(${blur}px)` : 'none',
      pointerEvents: 'auto' as const,
    };
  };

  const expandedTestimonial = testimonials.find(t => t.id === expandedId);

  return (
    <>
      {/* Main Carousel Container */}
      <div 
        ref={containerRef}
        className="relative overflow-hidden py-20 bg-background"
        style={{ 
          perspective: '1500px',
          width: '100vw',
          marginLeft: '-5rem',
        }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        role="region"
        aria-label="Success Stories Carousel"
        tabIndex={0}
      >
        {/* Carousel Track */}
        <div 
          className="relative h-[500px] md:h-[500px] sm:h-[400px]"
          style={{ transformStyle: 'preserve-3d' }}
        >
          {testimonials.map((testimonial, index) => {
            const position = getRelativePosition(index);
            const cardStyle = getCardStyle(position);
            const isCenter = position === 0;
            const isVisible = Math.abs(position) <= 2;

            if (!isVisible) return null;

            return (
              <div
                key={testimonial.id}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] md:w-[350px] sm:w-[280px] cursor-pointer"
                style={{
                  ...cardStyle,
                  transition: 'all 0.7s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  transformStyle: 'preserve-3d',
                }}
                onClick={() => handleCardClick(testimonial.id, position)}
              >
                <div
                  className={`relative bg-gradient-to-br from-card to-card/80 backdrop-blur-sm border rounded-2xl overflow-hidden h-[500px] md:h-[500px] sm:h-[400px] ${
                    isCenter
                      ? 'border-primary/60 shadow-3d'
                      : 'border-border/50 shadow-lg'
                  }`}
                  style={{
                    transformStyle: 'preserve-3d',
                    boxShadow: isCenter 
                      ? '0 10px 40px rgba(0,0,0,0.5), 0 0 30px rgba(255, 102, 0, 0.3)'
                      : '0 5px 20px rgba(0,0,0,0.3)',
                  }}
                >
                  {/* Card Image with Gradient Overlay */}
                  <div className="relative h-3/5 overflow-hidden">
                    <AppImage
                      src={testimonial.image}
                      alt={testimonial.alt}
                      className="w-full h-full object-cover"
                    />
                    {/* Gradient overlay for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    
                    {/* Content Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="text-xl font-bold text-white mb-2 uppercase tracking-wide">
                        {testimonial.company}
                      </h3>
                      <p className="text-sm text-white/90 mb-3">
                        {testimonial.position}
                      </p>
                      
                      {/* CTA Button - only on center card */}
                      {isCenter && (
                        <button className="px-6 py-2 bg-warning text-black font-bold rounded-lg hover:bg-warning/90 transition-all duration-300 flex items-center gap-2 group">
                          <Icon name="PlayIcon" size={16} variant="solid" />
                          <span>BEKIJK NU</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-6 h-2/5 flex flex-col justify-between">
                    <div>
                      {/* Rating */}
                      <div className="flex items-center space-x-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            name="StarIcon"
                            size={16}
                            variant={i < testimonial.rating ? 'solid' : 'outline'}
                            className={i < testimonial.rating ? 'text-warning' : 'text-muted-foreground'}
                          />
                        ))}
                      </div>

                      {/* Quote */}
                      <blockquote className="text-sm text-foreground leading-relaxed line-clamp-3">
                        "{testimonial.quote}"
                      </blockquote>
                    </div>

                    {/* Author */}
                    <div className="mt-4">
                      <p className="text-sm font-bold text-foreground">
                        {testimonial.author}
                      </p>
                    </div>
                  </div>

                  {/* Center Indicator */}
                  {isCenter && (
                    <div className="absolute top-4 right-4 animate-pulse">
                      <div className="w-3 h-3 bg-primary rounded-full shadow-orange-glow" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={navigatePrev}
          className="absolute left-8 top-1/2 -translate-y-1/2 z-[200] w-12 h-12 rounded-full bg-white/10 hover:bg-white/30 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-all duration-300 hover:scale-110 group"
          aria-label="Previous testimonial"
        >
          <Icon name="ChevronLeftIcon" size={24} className="text-white group-hover:text-primary transition-colors" />
        </button>

        <button
          onClick={navigateNext}
          className="absolute right-8 top-1/2 -translate-y-1/2 z-[200] w-12 h-12 rounded-full bg-white/10 hover:bg-white/30 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-all duration-300 hover:scale-110 group"
          aria-label="Next testimonial"
        >
          <Icon name="ChevronRightIcon" size={24} className="text-white group-hover:text-primary transition-colors" />
        </button>

        {/* Indicator Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[200] flex items-center gap-3">
          {testimonials.map((testimonial, index) => (
            <button
              key={testimonial.id}
              onClick={() => {
                setCurrentIndex(index);
                resetAutoPlay();
              }}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex
                  ? 'w-8 h-2 bg-primary' :'w-2 h-2 bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
              aria-current={index === currentIndex ? 'true' : 'false'}
            />
          ))}
        </div>

        {/* Gradient Edge Overlays */}
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-background to-transparent pointer-events-none z-[150]" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-background to-transparent pointer-events-none z-[150]" />
      </div>

      {/* Expanded Modal */}
      {expandedId && expandedTestimonial && (
        <div 
          className="fixed inset-0 z-[300] flex items-center justify-center bg-background/95 backdrop-blur-md animate-fadeIn"
          onClick={closeModal}
          style={{ perspective: '2000px' }}
        >
          <div 
            className="relative max-w-4xl w-full mx-4 animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
            style={{
              transformStyle: 'preserve-3d',
              animation: 'modalEnter 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div 
              className="relative bg-gradient-to-br from-card via-card to-card/90 border-2 border-primary/50 rounded-3xl overflow-hidden shadow-2xl shadow-primary/30"
              style={{ transform: 'translateZ(50px)' }}
            >
              {/* Animated Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-primary/10 animate-pulse" />
              
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-30 w-10 h-10 rounded-full bg-muted/80 hover:bg-primary/20 border border-border hover:border-primary/50 flex items-center justify-center transition-all duration-300 group"
                style={{ transform: 'translateZ(60px)' }}
                aria-label="Close modal"
              >
                <Icon name="XMarkIcon" size={20} className="text-foreground group-hover:text-primary transition-colors" />
              </button>

              <div className="relative z-10 p-8">
                {/* Modal Image */}
                <div className="relative h-80 rounded-2xl overflow-hidden mb-6">
                  <AppImage
                    src={expandedTestimonial.image}
                    alt={expandedTestimonial.alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <h2 className="text-4xl font-bold text-white mb-3 uppercase tracking-wide">
                      {expandedTestimonial.company}
                    </h2>
                    <p className="text-lg text-white/90">
                      {expandedTestimonial.position}
                    </p>
                  </div>
                </div>

                {/* Modal Content */}
                <div className="space-y-6">
                  {/* Rating */}
                  <div className="flex items-center space-x-2">
                    {[...Array(5)].map((_, i) => (
                      <Icon
                        key={i}
                        name="StarIcon"
                        size={24}
                        variant={i < expandedTestimonial.rating ? 'solid' : 'outline'}
                        className={i < expandedTestimonial.rating ? 'text-warning' : 'text-muted-foreground'}
                      />
                    ))}
                  </div>

                  {/* Full Quote */}
                  <blockquote className="text-lg text-foreground leading-relaxed">
                    "{expandedTestimonial.quote}"
                  </blockquote>

                  {/* Author Info */}
                  <div className="flex items-center gap-4 pt-4 border-t border-border/50">
                    <div className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-primary/50">
                      <AppImage
                        src={expandedTestimonial.image}
                        alt={expandedTestimonial.alt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-foreground">
                        {expandedTestimonial.author}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {expandedTestimonial.position}
                      </p>
                    </div>
                  </div>

                  {/* Video Link (if available) */}
                  {expandedTestimonial.videoUrl && (
                    <a
                      href={expandedTestimonial.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-lg transition-all duration-300"
                    >
                      <Icon name="PlayIcon" size={20} variant="solid" />
                      <span>Bekijk Video Testimonial</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TestimonialCarousel;